<script setup lang="ts">
import { inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { IconUserPlus } from '@tabler/icons-vue';
import { useLocaleStore } from '../../stores/locale';
import axiosApiClient from '../../axios';
import { permissions } from '../../data/permissions'

const { t } = useI18n();
const openAddEditModal = ref(false);
const localeStore = useLocaleStore();
const isbusy = ref(false);
const swal = inject('$swal') as any;

interface User {
    id: number;
    name: string;
    email: string;
    mobile?: string; // Optional, only needed for creating a new user
    type: string;
    password?: string; // Optional, only needed for creating a new user
    password_confirmation?: string; // Optional, only needed for creating a new user
    role?: number; // Optional, only needed for creating a new user    
}
interface Role {
    id: number;
    name: string;
    permissions: string; // JSON string of permissions
}
const users = ref<User[]>([]);
const user: User = {
    id: 0,
    name: '',
    email: '',
    mobile: '',
    type: '',
    password: '',
    password_confirmation: '',
    role: 0,
};
const user_permissions = ref<any[]>([]); // Initialize as an empty array

const fetchUsers = async () => {
    isbusy.value = true;
    try {
        const response = await axiosApiClient.get('/api/users');
        // Handle the response data as needed
        users.value = response.data;
    } catch (error) {
        console.error('Error fetching users:', error);
    }
};

onMounted(() => {
    clear();
    fetchUsers();
    fetchRoles();
    openAddEditModal.value = false;
    isbusy.value = false;
});
const addUser = () => {
    clear();
    user.id = 0;
    user.name = '';
    user.email = '';
    user.type = '';
    user.password = '';
    user.password_confirmation = '';
    user.role = 0;
    openAddEditModal.value = true;
};
const editUser = (userToEdit: User) => {
    clear();
    user.id = userToEdit.id;
    user.name = userToEdit.name;
    user.email = userToEdit.email;
    user.type = userToEdit.type;
    user.password = ''; // Reset password field
    user.role = userToEdit.role; // Set the role for editing
    openAddEditModal.value = true;
    fillPermissions();
};
const upsert = async (user: User) => {
    // Reset errors
    if (user.role === 0) {
        swal.fire({
            icon: 'error',
            title: t('Oops...'),
            position: 'top-end',
            toast: true,
            text: t('Please select a role!'),
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
        return;
    }
    try {
        user.password_confirmation = user.password;
        if (user.id > 0) {
            await axiosApiClient.put(`/api/users/${user.id}`, user);
        } else {
            await axiosApiClient.post('/api/users', user);
        }
        openAddEditModal.value = false;
        swal.fire({
            icon: 'success',
            title: t('Success'),
            position: 'top-end',
            toast: true,
            text: t('User saved successfully!'),
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        }).then(() => {
            saveRolePermissions();
            fetchUsers();
            clear();
            isbusy.value = false;
        });
    } catch (error) {
        console.error('Error saving user:', error);
        swal.fire({
            icon: 'error',
            title: t('Oops...'),
            position: 'top-end',
            toast: true,
            text: t('Something went wrong!'),
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
        isbusy.value = false;
        return;
    }
};
const roles = ref<Role[]>([]);
const fetchRoles = async () => {
    isbusy.value = true;
    try {
        const response = await axiosApiClient.get('/api/roles');
        roles.value = response.data;
    } catch (error) {
        console.error('Error fetching roles:', error);
    }
};
const showhidepassword = () => {
    const passwordField = document.getElementById('password') as HTMLInputElement;
    if (passwordField.type === 'password') {
        passwordField.type = 'text';
    } else {
        passwordField.type = 'password';
    }
};
const clear = () => {
    user.id = 0;
    user.name = '';
    user.email = '';
    user.type = '';
    user.password = '';
    user.password_confirmation = '';
    user.role = 0;

    user_permissions.value = permissions;
};
const fillPermissions = () => {
    let full_permissions_list = permissions.map((permission) => {
        return {
            module: permission.module,
            actions: permission.actions.map((action) => {
                return {
                    name: action.name,
                    value: false,
                };
            }),
        };
    });
    const selectedRole = roles.value.find((role) => role.id === user.role);
    if (selectedRole) {
        const selected_role_perms = JSON.parse(selectedRole.permissions ? selectedRole.permissions : '{}');
        
        full_permissions_list.forEach((permission) => {
            const selected_permission = selected_role_perms.find((perm: any) => perm.module === permission.module);
            if (selected_permission) {
                permission.actions.forEach((action) => {
                    if(selected_permission.actions.length === 0) {
                        action.value = false;
                    }
                    const selected_action = selected_permission.actions.find((act: any) => act.name === action.name);
                    if (selected_action) {
                        action.value = selected_action.value;
                    }
                });
            }
        });
        user_permissions.value = full_permissions_list;
    }
};
const saveRolePermissions = () => {
    //save in database
    const role = roles.value.find((role) => role.id === user.role);
    if (role) {
        role.permissions = JSON.stringify(user_permissions.value);
        axiosApiClient.put(`/api/roles/${role.id}`, role)
            .then(() => {
                swal.fire({
                    icon: 'success',
                    title: t('Success'),
                    position: 'top-end',
                    toast: true,
                    text: t('Permissions saved successfully!'),
                    showConfirmButton: false,
                    timer: 1500,
                    timerProgressBar: true,
                });
            })
            .catch((error) => {
                console.error('Error saving role permissions:', error);
            });
    }

};

</script>

<template>
    <admin-layout>
        <loading-spinner :is-loading="isbusy" />
        <div>
            <h1 class="text-2xl font-bold mb-4">{{ t('Users') }}</h1>
            <div class="mb-4 flex justify-end">
                <router-link to="#" @click="addUser"
                    class="bg-blue-500 text-white px-4 py-2 rounded flex">
                    {{ t('Create User') }}
                    <IconUserPlus stroke={2} />
                </router-link>
            </div>

            <table class="min-w-full bg-white border border-gray-300">
                <thead>
                    <tr>
                        <th class="border px-4 py-2">#</th>
                        <th class="border px-4 py-2">{{ t('Name') }}</th>
                        <th class="border px-4 py-2">{{ t('Email') }}</th>
                        <th class="border px-4 py-2">{{ t('Type') }}</th>
                        <th class="border px-4 py-2">{{ t('Role') }}</th>
                        <th class="border px-4 py-2"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(user, index) in users" :key="user.id">
                        <td class="border px-4 py-2">{{ index + 1 }}</td>
                        <td class="border px-4 py-2">{{ user.name }}</td>
                        <td class="border px-4 py-2">{{ user.email }}</td>
                        <td class="border px-4 py-2">{{ user.type }}</td>
                        <td class="border px-4 py-2">
                            {{t(roles.find((role) => role.id === user.role)?.name || '')}}
                        </td>
                        <td class="border px-4 py-2">
                            <button @click="editUser(user)" class="text-blue-500 hover:underline">
                                {{ t('Edit') }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="openAddEditModal" class="custom-modal overflow-auto" id="projectAddEditModal"
            @click.self="openAddEditModal = false">
            <div class="modal-content absolute h-[100%] overflow-auto"
                :dir="localeStore.locale == 'ar' ? 'rtl' : 'ltr'">
                <div class="modal-header">
                    <h4 class="font-bold">{{ user.id > 0 ? t('Edit User') : t('Add User') }}</h4>
                    <button @click="openAddEditModal = false" class="text-gray-500 hover:text-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="editor-wrapper">
                        <form @submit.prevent="upsert(user)">
                            <div class="mb-4">
                                <label for="name" class="block text-gray-700">{{ t('Name') }}</label>
                                <input type="text" v-model="user.name" id="name"
                                    class="border rounded w-full px-3 py-2" />
                            </div>
                            <div class="mb-4">
                                <label for="email" class="block text-gray-700">{{ t('Email') }}</label>
                                <input type="email" v-model="user.email" id="email" autocomplete="off"
                                    class="border rounded w-full px-3 py-2" />
                            </div>
                            <div class="mb-4">
                                <label for="mobile" class="block text-gray-700">{{ t('Mobile') }}</label>
                                <input type="text" v-model="user.mobile" id="mobile" autocomplete="off"
                                    class="border rounded w-full px-3 py-2" />
                            </div>
                            <div class="mb-4">
                                <label for="type" class="block text-gray-700">{{ t('Type') }}</label>
                                <select v-model="user.type" id="type" class="border rounded w-full px-3 py-2">
                                    <option value="" disabled>{{ t('Select Type') }}</option>
                                    <option value="admin">{{ t('Admin') }}</option>
                                    <option value="supervisor">{{ t('Supervisor') }}</option>
                                    <option value="user">{{ t('User') }}</option>
                                </select>
                            </div>
                            <div class="mb-4">
                                <label for="password" class="block text-gray-700">{{ t('Password') }}</label>
                                <input type="password" v-model="user.password" id="password" autocomplete="off"
                                    class="border rounded w-full px-3 py-2" />
                                <div class="flex gap-2">
                                    <input type="checkbox" id="showPassword" @click="showhidepassword">
                                    <label for="showPassword" class="ml-2 text-gray-700">
                                        {{ t('Show Password') }}</label>
                                </div>
                            </div>
                            <div class="mb-4 flex flex-col gap-2">
                                <label for="role" class="block text-gray-700">{{ t('Role') }}</label>
                                <select v-model="user.role" id="role" class="border rounded w-full px-3 py-2"
                                    @change="fillPermissions">
                                    <option value="0" disabled>{{ t('Select Role') }}</option>
                                    <option v-for="role in roles" :key="role.id" :value="role.id">
                                        {{ t(role.name) }}
                                    </option>
                                </select>
                                <div>
                                    <!-- Permissions -->
                                    <label for="permissions" class="block text-gray-700">{{ t('Permissions') }}</label>
                                    <!-- list of checkboxes-->
                                    <div class="flex flex-row flex-wrap gap-2">
                                        <div v-for="(permission, index) in user_permissions" :key="index"
                                            class="flex flex-col gap-1 border rounded px-3 py-2">
                                            <label for="">{{ t(permission.module) }}</label>
                                            <div class="flex gap-2">
                                                <div class="flex gap-1" v-for="(action, index) in permission.actions"
                                                    :key="index">
                                                    <input type="checkbox" :id="permission.module + action.name"
                                                        :name="action.name" v-model="action.value"
                                                        :checked="action.value">
                                                    <label :for="permission.module + action.name">{{ t(action.name)
                                                        }}</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button type="submit" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                                {{ user.id > 0 ? t('Update User') : t('Create User') }}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </admin-layout>
</template>


<style scoped>
.custom-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    @media (max-width: 768px) {
        top: 10%;
        left: 0;
        width: 100%;
        height: 100%;
        
    }
}

.custom-modal .modal-content {
    background-color: #fff;
    padding: 10px;
    border-radius: 5px;
    width: 80%;
    height: 80%;
    max-width: 500px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.custom-modal .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 5px;
}

.custom-modal .modal-header h2 {
    margin: 0;
}

.editor-wrapper {
    margin: 0 auto;
    padding: 1rem;
}

.modal-content {
    max-width: 800px !important;
    margin: 0 auto;
    padding: 20px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}
</style>